import { Chip } from '@mui/material';
import { PRESENTATION_STATE } from '../consts';

/*
 * Los dos tokens de abajo son SÓLO para `PALETA_ESTADO_CUIT_SVCC`, la paleta de
 * la grilla de backoffice de SVCC. El resto de los chips de la app —Empresas,
 * Presentaciones, Visitas, el drawer de la SRT— conserva sus colores de siempre.
 *
 * El problema que resuelven: medido contra la fila blanca, los fondos de chip de
 * la app están entre 1.04 y 1.22 de contraste. Los de color se distinguen igual
 * porque tienen TONO —rosa, azul, verde— y el ojo los separa por ahí. El gris no
 * tiene ninguno, así que a #EEEEEE (1.16) directamente desaparecía, y en la
 * grilla de SVCC el estado "Sin iniciar" es el más frecuente.
 *
 * Con el texto en el gris medio (#6B6B6B) no se podía oscurecer el fondo sin
 * romper la legibilidad. Con el texto casi negro sí: #C2C2C2 da 1.78 contra la
 * fila —se ve como pastilla— y el texto encima queda en 8.51.
 *
 * Por qué no se aplicó al resto: esos chips viven en pantallas que este cambio
 * no toca, y no se vio ningún reporte de que no se leyeran. Cambiar el color de
 * un estado en Empresas o en Visitas desde acá es una decisión de diseño de toda
 * la app, no un arreglo de esta grilla.
 */
const GRIS_FONDO = '#C2C2C2';

/** Texto de los chips de SVCC, casi negro: va sobre los rellenos con cuerpo. */
const TEXTO_CHIP = '#1E272E';

export enum CARCINOGENIC_SRT_STATES {
    PRESENTED = 1,
    ERROR = 2,
    PENDING = 3
}

interface PresentationStateChipProps {
    presentationState: PRESENTATION_STATE;
}

interface SRTPresentationStateChipProps {
    presentationState: CARCINOGENIC_SRT_STATES | null;
}

export type EstadoCuitSvccChipValue =
    | 'SIN_INICIAR'
    | 'RECHAZADA'
    | 'PRESENTADA_SIN_CONSOLIDAR'
    | 'CONSOLIDADA'
    | 'DESINCRONIZADA';

interface EstadoCuitSvccChipProps {
    estado: EstadoCuitSvccChipValue;
}

/**
 * Chip con el estilo de los chips de estado de la app —mismo alto, radio, peso y
 * padding—, pero con el texto y los colores que le pasen. Existe para que los
 * avisos del módulo SVCC se vean como el resto de la grilla sin duplicar el `sx`.
 */
export const ChipInformativo = ({
    label,
    paleta,
    testId,
    fullWidth = false
}: {
    label: string;
    paleta: { textColor: string; backgorundColor: string; borderColor?: string };
    testId?: string;
    /** Ocupa el ancho del contenedor, como un botón de la celda. */
    fullWidth?: boolean;
}) => (
    <Chip
        label={label}
        data-testid={testId}
        sx={{
            width: fullWidth ? '100%' : undefined,
            backgroundColor: paleta.backgorundColor,
            color: paleta.textColor,
            // El borde es lo que lo separa del fondo blanco de la fila.
            border: paleta.borderColor ? `1px solid ${paleta.borderColor}` : undefined,
            fontWeight: 600,
            fontSize: 12,
            height: 26,
            borderRadius: 3,
            '& .MuiChip-label': {
                px: 1.5
            }
        }}
    />
);

const presentationStateChipMap = () => ({
    [PRESENTATION_STATE.BORRADOR]: {
        textColor: '#1E272E',
        backgorundColor: '#E0EBED',
        text: 'Borrador'
    },
    [PRESENTATION_STATE.RECIBIDO]: {
        textColor: '#FF9900',
        backgorundColor: '#FFFBEA',
        text: 'Recibido'
    },
    [PRESENTATION_STATE.EN_REVISION]: {
        textColor: '#175AB6',
        backgorundColor: '#D9F1FF',
        text: 'En revisión'
    },
    [PRESENTATION_STATE.PENDIENTE_VISITA]: {
        textColor: '#904CE7',
        backgorundColor: '#DFDBF4',
        text: 'Pendiente Visita'
    },
    [PRESENTATION_STATE.PRESENTADO]: {
        textColor: '#2D6830',
        backgorundColor: '#E3F5E3',
        text: 'Presentado'
    },
    [PRESENTATION_STATE.CANCELADO]: {
        textColor: '#F44336',
        backgorundColor: '#FEF3F2',
        text: 'Rechazado'
    },
    [PRESENTATION_STATE.PROCESANDO]: {
        textColor: '#8C8C8C',
        backgorundColor: '#EEEEEE',
        text: 'Procesando'
    },
    [PRESENTATION_STATE.ERROR_PROCESAMIENTO]: {
        textColor: '#F44336',
        backgorundColor: '#FEF3F2',
        text: 'Error'
    },
    [PRESENTATION_STATE.INACTIVO]: {
        textColor: '#BB4D02',
        backgorundColor: '#FFFBEA',
        text: 'Inactivo'
    },
    [PRESENTATION_STATE.VISADO]: {
        textColor: '#1AB981',
        backgorundColor: '#EAFFFD',
        text: 'Visado'
    }
});

const srtPresentationStateChipMap = () => ({
    [CARCINOGENIC_SRT_STATES.PRESENTED]: {
        textColor: '#15803d',
        backgorundColor: '#BBF7D0',
        text: 'Generado'
    },
    [CARCINOGENIC_SRT_STATES.PENDING]: {
        textColor: '#c2410c',
        backgorundColor: '#fff7ed',
        text: 'Pendiente'
    },
    [CARCINOGENIC_SRT_STATES.ERROR]: {
        textColor: '#b91c1c',
        backgorundColor: '#fef2f2',
        text: 'Error'
    }
});

/**
 * Estados agregados por CUIT. Cada uno toma el TONO del estado de
 * `presentationStateChipMap` cuya semántica equivale —verde para consolidada,
 * rojo para rechazada—, pero invertido: el color va en el relleno y el texto
 * casi negro encima. Con el fondo casi blanco del original, en esta grilla el
 * chip no se leía como chip.
 *
 * Es la única paleta invertida de la app; el resto de las pantallas queda como
 * estaba. Se exporta para que los badges de filtro usen exactamente el mismo
 * color que el chip de la fila — si difieren, el filtro deja de leerse como "el
 * mismo estado".
 */
export const PALETA_ESTADO_CUIT_SVCC = {
    // gris, el tono de PROCESANDO
    SIN_INICIAR: {
        textColor: TEXTO_CHIP,
        backgorundColor: GRIS_FONDO,
        text: 'Sin iniciar'
    },
    // rojo, el tono de CANCELADO / ERROR_PROCESAMIENTO
    RECHAZADA: {
        textColor: TEXTO_CHIP,
        backgorundColor: '#EDB9B6',
        text: 'Con observaciones'
    },
    // azul, el tono de EN_REVISION: está en curso, falta cerrarla
    PRESENTADA_SIN_CONSOLIDAR: {
        textColor: TEXTO_CHIP,
        backgorundColor: '#ADC7EB',
        text: 'Presentada sin consolidar'
    },
    // verde, el tono de PRESENTADO
    CONSOLIDADA: {
        textColor: TEXTO_CHIP,
        backgorundColor: '#A0D0A2',
        text: 'Consolidada'
    },
    // violeta, el tono de PENDIENTE_VISITA. No la devuelve el backend como
    // estado: se deriva en el Front del resultado de "Verificar con SRT" (la
    // SRT consolidó y el guardado local falló) y requiere intervención manual.
    DESINCRONIZADA: {
        textColor: TEXTO_CHIP,
        backgorundColor: '#D2BCEE',
        text: 'Desincronizada'
    }
} as const;

const estadoCuitSvccChipMap = () => PALETA_ESTADO_CUIT_SVCC;

const PresentationStateChip = ({ presentationState }: PresentationStateChipProps) => {
    const chip = presentationStateChipMap()[presentationState as PRESENTATION_STATE] || {
        textColor: 'white',
        backgorundColor: 'gray',
        text: '-'
    };

    const { textColor, backgorundColor, text } = chip;

    return (
        <Chip
            label={text}
            sx={{
                backgroundColor: backgorundColor,
                color: textColor,
                fontWeight: 600,
                fontSize: 12,
                height: 26,
                borderRadius: 3,
                '& .MuiChip-label': {
                    px: 1.5
                }
            }}
        ></Chip>
    );
};

const PresentationSRTStateChip = ({ presentationState }: SRTPresentationStateChipProps) => {
    const chip = srtPresentationStateChipMap()[presentationState as CARCINOGENIC_SRT_STATES] || {
        textColor: 'white',
        backgorundColor: 'gray',
        text: '-'
    };

    const { textColor, backgorundColor, text } = chip;

    return (
        <Chip
            label={text}
            sx={{
                backgroundColor: backgorundColor,
                color: textColor,
                fontWeight: 600
            }}
        ></Chip>
    );
};

/**
 * Estado agregado por CUIT de la grilla SVCC de backoffice. Es un enum propio
 * (`EstadoCuitSvcc`), distinto del estado por presentación y del estado SRT por
 * informe, por eso tiene su propio mapa en vez de reusar los de arriba.
 */
const EstadoCuitSvccChip = ({ estado }: EstadoCuitSvccChipProps) => {
    const chip = estadoCuitSvccChipMap()[estado as EstadoCuitSvccChipProps['estado']] || {
        textColor: 'white',
        backgorundColor: 'gray',
        text: '-'
    };

    const { textColor, backgorundColor, text } = chip;

    return (
        <Chip
            label={text}
            size="small"
            sx={{
                backgroundColor: backgorundColor,
                color: textColor,
                fontWeight: 600,
                fontSize: 12,
                borderRadius: 3,
                '& .MuiChip-label': {
                    px: 1.5
                }
            }}
        />
    );
};

export { PresentationStateChip, PresentationSRTStateChip, EstadoCuitSvccChip };
